import * as THREE from 'three';
import GUI from 'lil-gui';
import HeroModule from '$src/js/modules/hero/HeroModule';

export class GuiService {
  private gui: GUI | null = null;

  readonly setup = (): void => {
    if (!import.meta.env.DEV) return;
    if (this.gui) this.gui.destroy();

    const gui = new GUI({ title: 'Dev Controls' });
    this.gui = gui;

    const sceneService = HeroModule.getInstance().scene;
    const modelService = HeroModule.getInstance().model;
    const markDirty = sceneService.markDirty;

    const jerseyModel = modelService.getJerseyModel();
    const displayGroup = sceneService.getDisplayGroup();

    if (jerseyModel && displayGroup) {
      const modelFolder = gui.addFolder('Display Group');
      modelFolder.add(displayGroup.scale, 'x', 0.1, 3, 0.01).name('Scale X').onChange(markDirty);
      modelFolder.add(displayGroup.scale, 'y', 0.1, 3, 0.01).name('Scale Y').onChange(markDirty);
      modelFolder.add(displayGroup.scale, 'z', 0.1, 3, 0.01).name('Scale Z').onChange(markDirty);
      modelFolder.add(displayGroup.position, 'x', -3, 3, 0.01).name('Position X').onChange(markDirty);
      modelFolder.add(displayGroup.position, 'y', -3, 3, 0.01).name('Position Y').onChange(markDirty);
      modelFolder.add(displayGroup.position, 'z', -3, 3, 0.01).name('Position Z').onChange(markDirty);

      const jerseyFolder = gui.addFolder('Model (inner)');
      jerseyFolder.add(jerseyModel.scale, 'x', 0.1, 3, 0.01).name('Scale X').onChange(markDirty);
      jerseyFolder.add(jerseyModel.scale, 'y', 0.1, 3, 0.01).name('Scale Y').onChange(markDirty);
      jerseyFolder.add(jerseyModel.scale, 'z', 0.1, 3, 0.01).name('Scale Z').onChange(markDirty);
      jerseyFolder.add(jerseyModel.position, 'x', -3, 3, 0.01).name('Position X').onChange(markDirty);
      jerseyFolder.add(jerseyModel.position, 'y', -3, 3, 0.01).name('Position Y').onChange(markDirty);
      jerseyFolder.add(jerseyModel.position, 'z', -3, 3, 0.01).name('Position Z').onChange(markDirty);
    }

    // const glassMat = modelService.getGlassMat();
    // if (glassMat) {
    //   const glassFolder = gui.addFolder('Glass Material');
    //   const markMatDirty = (): void => {
    //     glassMat.needsUpdate = true;
    //     markDirty();
    //   };

    //   glassFolder.add(glassMat, 'opacity', 0, 1, 0.01).name('Opacity').onChange(markMatDirty);
    //   glassFolder.add(glassMat, 'roughness', 0, 1, 0.01).name('Roughness').onChange(markMatDirty);
    //   glassFolder.add(glassMat, 'metalness', 0, 1, 0.01).name('Metalness').onChange(markMatDirty);
    //   glassFolder.add(glassMat, 'reflectivity', 0, 1, 0.01).name('Reflectivity').onChange(markMatDirty);
    //   glassFolder.add(glassMat, 'clearcoat', 0, 1, 0.01).name('Clearcoat').onChange(markMatDirty);
    //   glassFolder
    //     .add(glassMat, 'clearcoatRoughness', 0, 1, 0.01)
    //     .name('Clearcoat Rough')
    //     .onChange(markMatDirty);
    //   glassFolder
    //     .add(glassMat, 'envMapIntensity', 0, 3, 0.01)
    //     .name('Env Map Intensity')
    //     .onChange(markMatDirty);
    //   glassFolder
    //     .addColor({ color: '#' + glassMat.color.getHexString() }, 'color')
    //     .name('Color')
    //     .onChange((val: string) => {
    //       glassMat.color.set(val);
    //       markMatDirty();
    //     });
    // }

    // const glassReflectiveMat = modelService.getGlassReflectiveMat();
    // if (glassReflectiveMat) {
    //   const refFolder = gui.addFolder('Reflective Glass');
    //   const markRefDirty = (): void => {
    //     glassReflectiveMat.needsUpdate = true;
    //     markDirty();
    //   };

    //   refFolder.add(glassReflectiveMat, 'opacity', 0, 1, 0.01).name('Opacity').onChange(markRefDirty);
    //   refFolder
    //     .add(glassReflectiveMat, 'transmission', 0, 1, 0.01)
    //     .name('Transmission')
    //     .onChange(markRefDirty);
    //   refFolder
    //     .add(glassReflectiveMat, 'roughness', 0, 1, 0.01)
    //     .name('Roughness')
    //     .onChange(markRefDirty);
    //   refFolder
    //     .add(glassReflectiveMat, 'metalness', 0, 1, 0.01)
    //     .name('Metalness')
    //     .onChange(markRefDirty);
    //   refFolder
    //     .add(glassReflectiveMat, 'envMapIntensity', 0, 5, 0.01)
    //     .name('Env Map Intensity')
    //     .onChange(markRefDirty);
    //   refFolder
    //     .addColor({ color: '#' + glassReflectiveMat.color.getHexString() }, 'color')
    //     .name('Color')
    //     .onChange((val: string) => {
    //       glassReflectiveMat.color.set(val);
    //       markRefDirty();
    //     });
    // }

    const addLightFolder = (name: string, light: THREE.DirectionalLight | THREE.AmbientLight): void => {
      const folder = gui.addFolder(name);
      folder.add(light, 'intensity', 0, 50, 0.01).name('Intensity').onChange(markDirty);

      if (light instanceof THREE.DirectionalLight) {
        folder.add(light.position, 'x', -10, 10, 0.1).name('Position X').onChange(markDirty);
        folder.add(light.position, 'y', -10, 10, 0.1).name('Position Y').onChange(markDirty);
        folder.add(light.position, 'z', -10, 10, 0.1).name('Position Z').onChange(markDirty);
      }
      folder.close();
    };

    // const ambientLight = sceneService.getAmbientLight();
    // const keyLight = sceneService.getKeyLight();
    const fillLight = sceneService.getFillLight();
    // const rimLight = sceneService.getRimLight();

    // if (ambientLight) addLightFolder('Ambient Light', ambientLight);
    // if (keyLight) addLightFolder('Key Light', keyLight);
    if (fillLight) addLightFolder('Fill Light', fillLight);
    // if (rimLight) addLightFolder('Rim Light', rimLight);

    const envMap = modelService.getEnvMap();
    const scene = sceneService.getScene();
    if (envMap && scene) {
      const envFolder = gui.addFolder('Environment');
      const envState = { showBackground: false, rotationY: 1.58 };

      envFolder
        .add(envState, 'showBackground')
        .name('Show Background')
        .onChange((val: boolean) => {
          scene.background = val ? envMap : null;
          markDirty();
        });

      envFolder
        .add(envState, 'rotationY', -Math.PI, Math.PI, 0.01)
        .name('Rotation Y')
        .onChange((val: number) => {
          scene.environmentRotation.y = val;
          scene.backgroundRotation.y = val;
          markDirty();
        });

      envFolder.open();
    }
  };

  readonly dispose = (): void => {
    if (this.gui) {
      this.gui.destroy();
      this.gui = null;
    }
  };
}
